import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { AccountGate } from '../account-gate.data'

/**
 * The last session-ending answer the API gave (decisions 15 / 16). The HTTP interceptor reports;
 * `FairAccountSessionHost` (AppLayout) signs out locally and shows the message / restore screen,
 * so the interceptor never imports the router or the UI. Repeated reports of the same gate (a
 * page fires several calls at once) collapse into one until it is handled.
 */
export const useAccountGateStore = defineStore('accountGate', () => {
  const pending = ref<AccountGate | null>(null)
  /** The restore screen's date when the 403 came from a call (not from the e-mail link). */
  const restoreAvailableUntil = ref<string | null>(null)
  /** Restore token from the e-mail link (`/account/restore?token=`). */
  const restoreToken = ref<string | null>(null)
  /**
   * Decisions 8.1.2 / 8.1.5: the screen right after `DELETE /user` — "deleted on {date}. Cancel" with
   * the answer's `restore_token`. While it is open this session is NOT signed out by a 403
   * `account_pending_deletion` (the API refuses its token for everything but the cancel).
   */
  const cancelWindow = ref<{ token: string; deadline: string } | null>(null)
  /**
   * Decision 8.3: the server could not open the account right now (503 `provisioning_unavailable`
   * after the bounded retries) — the host shows "Try again"; the session stays. A counter, so a
   * second outage after a handled one is a new report.
   */
  const provisioningUnavailable = ref(0)
  /** Why the sign-in screen opened by itself (shown above its fields until the next sign-in). */
  const sessionNotice = ref<'token_revoked' | 'user_not_provisioned' | null>(null)

  function setSessionNotice(notice: 'token_revoked' | 'user_not_provisioned' | null) {
    sessionNotice.value = notice
  }

  function report(gate: AccountGate) {
    if (pending.value?.kind === gate.kind) return
    pending.value = gate
    if (gate.kind === 'scheduled_for_deletion') restoreAvailableUntil.value = gate.restoreAvailableUntil
  }

  function take(): AccountGate | null {
    const gate = pending.value
    pending.value = null
    return gate
  }

  function setRestoreToken(token: string | null) {
    restoreToken.value = token && token.trim() ? token.trim() : null
  }

  function reportProvisioningUnavailable() {
    provisioningUnavailable.value += 1
  }

  function openCancelWindow(token: string, deadline: string) {
    cancelWindow.value = { token, deadline }
  }

  function closeCancelWindow() {
    cancelWindow.value = null
  }

  return {
    pending,
    restoreAvailableUntil,
    restoreToken,
    sessionNotice,
    cancelWindow,
    provisioningUnavailable,
    report,
    reportProvisioningUnavailable,
    take,
    setRestoreToken,
    setSessionNotice,
    openCancelWindow,
    closeCancelWindow,
  }
})
